/**
 * Filter Rules API Client
 *
 * API functions for managing automatic filter rules.
 */

import createDebug from 'debug';
import { authFetch } from './client';

const debug = createDebug('app:api:filters');
const API_BASE = '/api';

// =============================================================================
// Types
// =============================================================================

export type ConditionType =
	| 'url_domain'
	| 'url_path'
	| 'url_pattern'
	| 'has_tag'
	| 'missing_tag'
	| 'title_contains'
	| 'description_contains'
	| 'source_equals'
	| 'metadata_field';

export type ActionType =
	| 'add_tag'
	| 'remove_tag'
	| 'set_queue'
	| 'archive'
	| 'delete'
	| 'webhook';

export interface Condition {
	type: ConditionType;
	value: string;
	field?: string; // For metadata_field type
	case_sensitive?: boolean;
	negate?: boolean;
}

export interface WebhookConfig {
	url: string;
	method?: 'POST' | 'PUT';
	headers?: Record<string, string>;
	include_link_data?: boolean;
}

export interface Action {
	type: ActionType;
	value: string | string[] | WebhookConfig | boolean;
}

export interface FilterRule {
	id: number;
	name: string;
	description: string | null;
	enabled: boolean;
	priority: number;
	conditions: Condition[];
	actions: Action[];
	stopProcessing: boolean;
	matchCount: number;
	lastRunAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface CreateFilterRuleInput {
	name: string;
	description?: string;
	enabled?: boolean;
	priority?: number;
	conditions: Condition[];
	actions: Action[];
	stop_processing?: boolean;
}

export interface UpdateFilterRuleInput {
	name?: string;
	description?: string | null;
	enabled?: boolean;
	priority?: number;
	conditions?: Condition[];
	actions?: Action[];
	stop_processing?: boolean;
}

export interface RunRuleResult {
	rule: { id: number; name: string };
	dryRun: boolean;
	totalLinks: number;
	matchedLinks: number;
	results: Array<{
		linkId: number;
		matched: boolean;
		actions: Array<{
			type: ActionType;
			success: boolean;
			error?: string;
			details?: Record<string, unknown>;
		}>;
	}>;
}

export interface TestConditionsResult {
	total: number;
	matched: number;
	results: Array<{
		linkId: number;
		url: string;
		title: string | null;
		matched: boolean;
	}>;
}

// =============================================================================
// Constants
// =============================================================================

export const CONDITION_LABELS: Record<ConditionType, string> = {
	url_domain: 'Domain equals/contains',
	url_path: 'Path matches (regex)',
	url_pattern: 'URL matches (regex)',
	has_tag: 'Has tag',
	missing_tag: 'Missing tag',
	title_contains: 'Title contains',
	description_contains: 'Description contains',
	source_equals: 'Source equals',
	metadata_field: 'Metadata field',
};

export const ACTION_LABELS: Record<ActionType, string> = {
	add_tag: 'Add tag(s)',
	remove_tag: 'Remove tag(s)',
	set_queue: 'Move to queue',
	archive: 'Archive link',
	delete: 'Delete link',
	webhook: 'Send webhook',
};

export const LINK_SOURCES = [
	{ value: 'imessage', label: 'iMessage' },
	{ value: 'api', label: 'API' },
	{ value: 'quick-add', label: 'Quick Add' },
	{ value: 'shortcut', label: 'Shortcut' },
	{ value: 'manual', label: 'Manual' },
	{ value: 'file', label: 'File' },
	{ value: 'url', label: 'URL' },
];

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all filter rules for the current user
 * GET /api/filters
 */
export async function listFilterRules(): Promise<{ rules: FilterRule[] }> {
	debug('listFilterRules');

	const response = await authFetch(`${API_BASE}/filters`);

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to list filter rules: ${response.status}`);
	}

	return response.json();
}

/**
 * Get a single filter rule
 * GET /api/filters/:id
 */
export async function getFilterRule(id: number): Promise<{ rule: FilterRule }> {
	debug('getFilterRule: %d', id);

	const response = await authFetch(`${API_BASE}/filters/${id}`);

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to get filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Create a new filter rule
 * POST /api/filters
 */
export async function createFilterRule(
	input: CreateFilterRuleInput
): Promise<{ rule: FilterRule }> {
	debug('createFilterRule: %s', input.name);

	const response = await authFetch(`${API_BASE}/filters`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(input),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to create filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Update a filter rule
 * PATCH /api/filters/:id
 */
export async function updateFilterRule(
	id: number,
	input: UpdateFilterRuleInput
): Promise<{ rule: FilterRule }> {
	debug('updateFilterRule: %d', id);

	const response = await authFetch(`${API_BASE}/filters/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(input),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to update filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Delete a filter rule
 * DELETE /api/filters/:id
 */
export async function deleteFilterRule(id: number): Promise<{ success: boolean }> {
	debug('deleteFilterRule: %d', id);

	const response = await authFetch(`${API_BASE}/filters/${id}`, {
		method: 'DELETE',
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to delete filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Toggle enabled status for a filter rule
 * POST /api/filters/:id/toggle
 */
export async function toggleFilterRule(id: number): Promise<{ rule: FilterRule }> {
	debug('toggleFilterRule: %d', id);

	const response = await authFetch(`${API_BASE}/filters/${id}/toggle`, {
		method: 'POST',
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to toggle filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Duplicate a filter rule
 * POST /api/filters/:id/duplicate
 */
export async function duplicateFilterRule(
	id: number,
	newName?: string
): Promise<{ rule: FilterRule }> {
	debug('duplicateFilterRule: %d %s', id, newName);

	const response = await authFetch(`${API_BASE}/filters/${id}/duplicate`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ name: newName }),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to duplicate filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Reorder filter rules
 * POST /api/filters/reorder
 */
export async function reorderFilterRules(orderedIds: number[]): Promise<{ success: boolean }> {
	debug('reorderFilterRules: %o', orderedIds);

	const response = await authFetch(`${API_BASE}/filters/reorder`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ orderedIds }),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to reorder filter rules: ${response.status}`);
	}

	return response.json();
}

/**
 * Run a filter rule on links
 * POST /api/filters/:id/run
 */
export async function runFilterRule(
	id: number,
	options: { linkIds?: number[]; dryRun?: boolean; limit?: number } = {}
): Promise<RunRuleResult> {
	debug('runFilterRule: %d %o', id, options);

	const response = await authFetch(`${API_BASE}/filters/${id}/run`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(options),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to run filter rule: ${response.status}`);
	}

	return response.json();
}

/**
 * Test filter conditions against links (preview matches)
 * POST /api/filters/test
 */
export async function testFilterConditions(
	conditions: Condition[],
	options: { linkIds?: number[]; limit?: number } = {}
): Promise<TestConditionsResult> {
	debug('testFilterConditions: %o', conditions);

	const response = await authFetch(`${API_BASE}/filters/test`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ conditions, ...options }),
	});

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to test filter conditions: ${response.status}`);
	}

	return response.json();
}
